import { useDataProvider } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import { MenuItem, Stack, TextField } from '@mui/material';
import type { CrmDataProvider } from '@/providers/types';
import type { CardProjectOption } from '@/types';
import { ProjectSearchAutocomplete } from '@/modules/receipts/ProjectSearchAutocomplete';
import type { SimpleRecord } from '@/modules/receipts/receiptHelpers';

interface CardProjectFieldsProps {
    project: CardProjectOption | null;
    fieldStepId: number | null;
    onChange: (project: CardProjectOption | null, fieldStepId: number | null) => void;
    disabled?: boolean;
}

/** Controlled project picker + Field step picker. Used by the create form and CardShow edit. */
export const CardProjectFields = ({
    project,
    fieldStepId,
    onChange,
    disabled = false,
}: CardProjectFieldsProps) => {
    const dataProvider = useDataProvider<CrmDataProvider>();
    const projectId = project?.id ?? null;

    const { data: fieldSteps = [] } = useQuery({
        queryKey: ['cardFieldSteps', projectId],
        queryFn: async () =>
            projectId == null ? [] : dataProvider.getCardFieldSteps(projectId),
        enabled: projectId != null,
    });

    const toCardProject = (rec: SimpleRecord | null): CardProjectOption | null =>
        rec ? { id: rec.id, name: rec.name ?? '', project_no: rec.project_no ?? null } : null;

    return (
        <Stack spacing={2} sx={{ width: '100%' }}>
            <ProjectSearchAutocomplete
                value={projectId != null ? String(projectId) : ''}
                onChange={id => {
                    if (!id) onChange(null, null);
                }}
                onProjectSelect={rec => {
                    const next = toCardProject(rec);
                    onChange(next, next?.id === projectId ? fieldStepId : null);
                }}
                label="Project"
                disabled={disabled}
                extraFilter={{ phase: 'active' }}
            />

            <TextField
                select
                fullWidth
                label="Field step"
                value={fieldSteps.some(step => step.id === fieldStepId) ? fieldStepId : ''}
                disabled={disabled || projectId == null}
                onChange={e =>
                    onChange(project, e.target.value === '' ? null : Number(e.target.value))
                }
                helperText={projectId == null ? 'Select a project first' : undefined}
            >
                <MenuItem value="">None</MenuItem>
                {fieldSteps.map(step => (
                    <MenuItem key={step.id} value={step.id}>
                        {step.name}
                    </MenuItem>
                ))}
            </TextField>
        </Stack>
    );
};

CardProjectFields.displayName = 'CardProjectFields';
